import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const WorkingMemoryTest = () => {
  const navigate = useNavigate();

  const [service, setService] = useState("");
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================
  // SCROLL TO TOP
  // ==========================================

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ==========================================
  // ACCESS CHECK + SERVICE
  // ==========================================

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      const email = localStorage.getItem("user_email");
      const authed = localStorage.getItem("rg_auth") === "1";

      if (!authed || !email) {
        navigate("/dashboard-login");
        return;
      }

      try {
        const res = await axios.get("/check-access", {
          params: {
            email,
            test: "working_memory",
          },
        });

        console.log("Working Memory API:", res.data);

        if (!res.data.allowed) {
          if (isMounted) {
            setHasAccess(false);
          }
          return;
        }

        // Get service from API response
        const userService =
          res.data.service ||
          res.data.metadata?.service ||
          res.data.user?.metadata?.service ||
          localStorage.getItem("service") ||
          "";

        if (isMounted) {
          setService(userService);
          setHasAccess(true);
        }
      } catch (err) {
        console.error(
          "Working memory access check failed:",
          err
        );

        if (isMounted) {
          setHasAccess(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ==========================================
  // SERVICE IDENTIFICATION
  // ==========================================

  const normalizedService = service.trim().toLowerCase();

  const isVisionIgnite =
    normalizedService === "forge your peak - vision ignite";

  const isIgniteOrAscend =
    normalizedService === "forge your peak - ignite" ||
    normalizedService === "forge your peak - ascend";

  // ==========================================
  // RETURN HOME
  // ==========================================

  const returnHome = () => {
    if (isVisionIgnite) {
      navigate("/measure");
    } else if (isIgniteOrAscend) {
      navigate("/ignite/dashboard");
    } else {
      navigate("/");
    }
  };

  // ==========================================
  // START TEST
  // ==========================================

  const startTest = () => {
    navigate("/working-memory/play");
  };

  // ==========================================
  // LOADING
  // ==========================================

  if (loading || hasAccess === null) {
    return (
      <div className="rg-login-wrapper">
        <p style={{ textAlign: "center" }}>
          Checking access...
        </p>
      </div>
    );
  }

  // ==========================================
  // ACCESS DENIED
  // ==========================================

  if (hasAccess === false) {
    return (
      <div className="access-denied-container">
        <h2>🚫 Access Denied</h2>

        <p>You don't have access to this test.</p>

        <button
          type="button"
          onClick={() => navigate("/")}
        >
          Go Back
        </button>
      </div>
    );
  }

  // ==========================================
  // SERVICE-BASED CONTENT
  // Ignite + Ascend = Original content
  // Vision Ignite = Separate content
  // ==========================================

  const pageTitle = isVisionIgnite
    ? "Working Memory Assessment"
    : "Working Memory Test";

  const pageDescription = isVisionIgnite
    ? "Working memory is your ability to temporarily hold and use information. This assessment helps you explore your memory, attention, and information-processing performance."
    : "Working memory measures your ability to temporarily store and recall information. It trains the brain to Observe → Hold → Recall.";

  return (
    <div className="rg-login-wrapper">

      {/* ==========================================
          RETURN HOME BUTTON
      ========================================== */}

      <div className="return-home-wrapper">
        <button
          type="button"
          className="return-home-btn"
          onClick={returnHome}
        >
          ← Return Home
        </button>
      </div>

      {/* ==========================================
          HEADER SECTION
      ========================================== */}

      <div className="reaction-header-card">

        <h2 className="reaction-title">
          {pageTitle}
        </h2>

        <p className="reaction-subtitle">
          {pageDescription}
        </p>

        <div className="reaction-pill">
          Test type:{" "}
          <strong>
            Memory → Working Memory Capacity
          </strong>
        </div>

        <hr className="reaction-divider" />

        {/* ==========================================
            WHY SECTION
        ========================================== */}

        <div className="reaction-why-card">

          <h3 className="why-title">
            {isVisionIgnite
              ? "Why Working Memory Matters"
              : "Why working memory matters"}
          </h3>

          {isVisionIgnite ? (
            <>
              {/* VISION IGNITE CONTENT */}

              <p className="why-description">
                Working memory helps you temporarily retain
                information while processing and using it.
                It supports attention, recall, and the ability
                to respond to information presented in real time.
              </p>

              <div className="why-tags">
                <span>Memory Retention</span>
                <span>Visual Attention</span>
                <span>Information Processing</span>
                <span>Quick Recall</span>
                <span>Concentration</span>
              </div>

              <ul className="why-list">
                <li>
                  <strong>Information retention:</strong>{" "}
                  Hold information in your mind for a short period.
                </li>

                <li>
                  <strong>Attention:</strong>{" "}
                  Focus on information while preparing to recall it.
                </li>

                <li>
                  <strong>Mental processing:</strong>{" "}
                  Retain and use information as you complete a task.
                </li>

                <li>
                  <strong>Recall accuracy:</strong>{" "}
                  Observe how accurately you reproduce information.
                </li>
              </ul>
            </>
          ) : (
            <>
              {/* ORIGINAL IGNITE + ASCEND CONTENT */}

              <p className="why-description">
                Working memory is essential for learning,
                decision making, and performance under pressure.
                It helps you retain and use information in real
                time — critical in sports, academics, and everyday
                problem solving.
              </p>

              <div className="why-tags">
                <span>Stronger memory retention</span>
                <span>Better focus</span>
                <span>Faster thinking</span>
                <span>Improved decision making</span>
                <span>Enhanced concentration</span>
              </div>
            </>
          )}

        </div>
      </div>

      {/* ==========================================
          HOW GAME WORKS
      ========================================== */}

      <div className="how-card">

        <h2 className="how-title">
          How the game works
        </h2>

        <ul className="how-list">

          <li>
            A sequence of numbers will be displayed on the screen.
          </li>

          <li>
            Memorize the sequence carefully.
          </li>

          <li>
            Once it disappears, enter the sequence correctly.
          </li>

          <li>
            The sequence length increases with each level.
          </li>

          <li>
            The game ends when you make a mistake.
          </li>

        </ul>

        <p className="how-note">
          Tip: Stay calm and focused for better recall.
        </p>

      </div>

      {/* ==========================================
          START TEST
      ========================================== */}

      <div className="start-test-section">

        <h2>
          {isVisionIgnite
            ? "Ready to Assess Your Working Memory?"
            : "Ready to test your memory?"}
        </h2>

        <button
          type="button"
          className="start-test-btn"
          onClick={startTest}
        >
          Start Test
        </button>

      </div>

    </div>
  );
};

export default WorkingMemoryTest;